/**
 * The human half of the panel: who is signed in, and who is allowed to be.
 *
 * WHY THIS IS A SECOND CLIENT AND NOT MORE OF api.ts. Everything in api.ts is a
 * projection of edge-api, which the panel worker proxies with a machine key
 * attached server-side. The routes below are answered by the panel ITSELF — they
 * are about the person at the keyboard, they set and clear a cookie, and the
 * `/auth/*` ones never reach edge-api at all. Two servers, two files. The
 * request helper and the error class are shared because the error envelope is
 * shared; nothing else is.
 *
 * `/auth/*` IS DELIBERATELY NOT UNDER `/api`, so `base: ''` on every call in
 * `auth` below is load-bearing rather than a shortcut: the prefix is how a
 * reader tells which server holds the session. `/api/users` keeps the proxy
 * prefix because the contract puts it there — the panel intercepts that path
 * instead of forwarding it, which is worth knowing but is not the client's call
 * to make.
 *
 * THE USER-MANAGEMENT WRITES RETURN `unknown` ON PURPOSE. The contract fixes a
 * response shape for every `/auth/*` route and for none of the writes under
 * `/api/users` — a PATCH answering with the updated row and a PATCH answering
 * 204 are both within it. So no screen reads them: every mutation is followed by
 * a re-read of the list, which costs one GET and is the only version of the
 * table that is certainly the server's. Declaring a shape here that the server
 * had not promised would put a lie in the type system, and the first symptom
 * would be a row rendering as `undefined` after a successful save.
 *
 * NO RETRIES, AND EVERY CALL TAKES AN AbortSignal — the api.ts rules, unchanged.
 * The signal earns its keep here more than anywhere else: `me()` is the one
 * request this app makes on mount before anything else exists, so StrictMode
 * double-invokes it on every development reload.
 */
import { ApiError, req } from '@/api'

/** Descending authority. `admin` > `power` > `viewer`. */
export type Role = 'admin' | 'power' | 'viewer'

export interface PanelUser {
  id: string
  email: string
  name: string | null
  role: Role
  /** True when invited but never registered — no password has been set yet. */
  pending: boolean
  disabled: boolean
  createdAt: string
  lastLoginAt: string | null
  /** Present only on `GET /api/users`, and only in the admin view. */
  inviteExpiresAt?: string | null
}

/**
 * What an invite was issued FOR, read back from the token before it is accepted.
 *
 * The email and the role are the invite itself, not a suggestion, which is why
 * the accept screen renders both as text and neither as a field. A form that let
 * the holder of a link edit either would be a form that mints an admin account
 * at an address of the holder's choosing.
 */
export interface InvitePreview {
  email: string
  name: string | null
  role: Role
}

export interface InviteRequest {
  email: string
  role: Role
  /** Optional. The invited person sets their own name when they accept. */
  name?: string
}

export interface UserPatch {
  role?: Role
  name?: string
  disabled?: boolean
}

export const auth = {
  /**
   * The signed-in user, or null when nobody is.
   *
   * A 401 IS NOT AN ERROR HERE AND IS NOT THROWN. "There is no session" is the
   * ordinary answer on a cold load and it is precisely the question the login
   * gate is asking, so returning it as a value keeps the `instanceof ApiError`
   * unpacking out of every consumer — and keeps a perfectly normal first visit
   * from rendering a red box. Anything else (a 500, a dead network) still
   * throws, because that is genuinely a failure and not an answer.
   */
  async me(signal: AbortSignal): Promise<PanelUser | null> {
    try {
      const { user } = await req<{ user: PanelUser }>('/auth/me', { signal, base: '' })
      return user
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null
      throw e
    }
  },

  /**
   * Sign in, and set the session cookie.
   *
   * The returned user is NOT proof that the browser kept the cookie — see the
   * note on `refresh` in auth-provider.tsx, which is why a successful login is
   * followed by a `me()` rather than by trusting this.
   */
  async login(email: string, password: string, signal: AbortSignal): Promise<PanelUser> {
    const { user } = await req<{ user: PanelUser }>('/auth/login', {
      signal,
      base: '',
      method: 'POST',
      body: { email, password },
    })
    return user
  },

  /** Clears the cookie server-side. The cookie is HttpOnly, so only the server can. */
  logout(signal: AbortSignal): Promise<void> {
    return req<void>('/auth/logout', { signal, base: '', method: 'POST' })
  },

  /**
   * Ask for a reset link.
   *
   * ANSWERS 202 WHETHER OR NOT THE ADDRESS EXISTS, so success here means the
   * request was accepted and nothing more. A caller learns nothing about the
   * address from it and must not imply that it did; that is the entire reason
   * the endpoint is shaped this way, and the reason ForgotPassword.tsx has one
   * outcome instead of two.
   */
  forgotPassword(email: string, signal: AbortSignal): Promise<{ ok: true }> {
    return req<{ ok: true }>('/auth/forgot-password', {
      signal,
      base: '',
      method: 'POST',
      body: { email },
    })
  },

  resetPassword(token: string, password: string, signal: AbortSignal): Promise<void> {
    return req<void>('/auth/reset-password', {
      signal,
      base: '',
      method: 'POST',
      body: { token, password },
    })
  },

  /** Read an invite without consuming it, so the accept screen can name it. */
  readInvite(token: string, signal: AbortSignal): Promise<InvitePreview> {
    return req<InvitePreview>(`/auth/invite?token=${encodeURIComponent(token)}`, {
      signal,
      base: '',
    })
  },

  /** Consumes the invite, sets the password AND signs the person in. */
  async acceptInvite(
    body: { token: string; name: string; password: string },
    signal: AbortSignal,
  ): Promise<PanelUser> {
    const { user } = await req<{ user: PanelUser }>('/auth/accept-invite', {
      signal,
      base: '',
      method: 'POST',
      body,
    })
    return user
  },

  changePassword(current: string, next: string, signal: AbortSignal): Promise<void> {
    return req<void>('/auth/change-password', {
      signal,
      base: '',
      method: 'POST',
      body: { currentPassword: current, newPassword: next },
    })
  },
}

export const users = {
  /** Readable by any role. The write methods below are admin-only, server-side. */
  list(signal: AbortSignal): Promise<{ rows: PanelUser[] }> {
    return req<{ rows: PanelUser[] }>('/users', { signal })
  },

  /** Creates a pending user and sends them an invite link. */
  invite(body: InviteRequest, signal: AbortSignal): Promise<unknown> {
    return req<unknown>('/users', { signal, method: 'POST', body })
  },

  patch(id: string, patch: UserPatch, signal: AbortSignal): Promise<unknown> {
    return req<unknown>(`/users/${encodeURIComponent(id)}`, {
      signal,
      method: 'PATCH',
      body: patch,
    })
  },

  remove(id: string, signal: AbortSignal): Promise<unknown> {
    return req<unknown>(`/users/${encodeURIComponent(id)}`, { signal, method: 'DELETE' })
  },

  /** Issues a fresh token. The old link stops working. */
  resendInvite(id: string, signal: AbortSignal): Promise<unknown> {
    return req<unknown>(`/users/${encodeURIComponent(id)}/resend-invite`, {
      signal,
      method: 'POST',
    })
  },

  /** Signs the user out of every browser they are signed in on. */
  revokeSessions(id: string, signal: AbortSignal): Promise<unknown> {
    return req<unknown>(`/users/${encodeURIComponent(id)}/revoke-sessions`, {
      signal,
      method: 'POST',
    })
  },
}

/**
 * What a role may do, as far as the UI is concerned.
 *
 * TWO CAPABILITIES, NOT A PERMISSION SYSTEM. The contract has exactly three
 * roles and exactly two lines between them: a viewer may send GET and HEAD and
 * nothing else, and user management is admin-only. Anything finer-grained here
 * would be a model of a server that does not have one.
 *
 * AND IT IS A UI PREDICATE, NOT A SECURITY BOUNDARY. The panel decides; `can`
 * only decides whether to draw a control that the panel would refuse. That
 * distinction is why it is worth being exact about — a button gated on the wrong
 * capability is a 403 in front of somebody who was told they could, and a screen
 * gated on the right one never has to render an error at all.
 */
export type Capability = 'write' | 'manageUsers'

const GRANTS: Record<Role, readonly Capability[]> = {
  admin: ['write', 'manageUsers'],
  power: ['write'],
  viewer: [],
}

/**
 * Whether this user may do this thing. No user, no capability.
 *
 * `disabled` IS NOT CONSULTED, deliberately. Whether an account may hold a
 * session at all is the session layer's question and the panel answers it on
 * every request; role is what this function is about. Folding the two together
 * here would make `can` a second, partial copy of a check that already exists
 * server-side, and a partial copy of an authorisation check is worse than none.
 */
export function can(user: PanelUser | null | undefined, capability: Capability): boolean {
  if (!user) return false
  // A role this bundle does not recognise grants nothing. The panel and the
  // bundle deploy together so it should not arise, but a tab left open across a
  // deploy that adds a role is how it would: failing closed shows that person
  // the read-only panel rather than a screenful of buttons the server refuses.
  return GRANTS[user.role]?.includes(capability) ?? false
}
